import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Receipt, Store, CreditCard, QrCode, Banknote, X, ChevronDown, ChevronRight,
  Calendar, Clock, Hash, Terminal, MapPin, ShoppingBag, CheckCircle2,
  XCircle, AlertCircle, Loader2, TimerReset, RefreshCw, FileText, Send,
  Copy, Check, Ban, Eye, EyeOff, Wifi, RotateCcw, Archive, ArchiveRestore, Download,
} from 'lucide-react';
import { useOrders, useOrdersArchive } from '../hooks/useOrders.js';
import { useStores } from '../hooks/useStores.js';
import { useAuthStore } from '../store/auth.store.js';
import { useClientStore } from '../store/client.store.js';
import { cancelOrder as apiCancelOrder, getOrderMpStatus, refundOrder as apiRefundOrder, archiveBatch as apiArchiveBatch, exportOrdersArchive as apiExportArchive } from '../api/orders.js';
import NoClientSelected from '../components/NoClientSelected.jsx';
import Spinner from '../components/Spinner.jsx';

/* ── Status metadata ──────────────────────────────────────── */

// Valores exactos tal como están en la DB
const ORDER_STATUS = {
  created:   { icon: TimerReset,   color: 'text-blue-400',    bg: 'bg-blue-400/10',    border: 'border-blue-400/30',   pulse: false },
  processed: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/30',pulse: false },
  canceled:  { icon: XCircle,      color: 'text-red-400',     bg: 'bg-red-400/10',     border: 'border-red-400/30',    pulse: false },
  // valores intermedios que puede enviar la API de MP antes de resolver
  open:      { icon: Loader2,      color: 'text-sky-400',     bg: 'bg-sky-400/10',     border: 'border-sky-400/30',    pulse: true  },
  at_terminal:{ icon: Terminal,    color: 'text-amber-400',   bg: 'bg-amber-400/10',   border: 'border-amber-400/30',  pulse: true  },
  processing: { icon: RefreshCw,   color: 'text-amber-300',   bg: 'bg-amber-300/10',   border: 'border-amber-300/30',  pulse: true  },
};

const PAYMENT_STATUS = {
  created:    { color: 'text-blue-400'    },
  pending:    { color: 'text-amber-400'   },
  approved:   { color: 'text-emerald-400' },
  rejected:   { color: 'text-red-400'     },
  canceled:   { color: 'text-red-400'     },
  cancelled:  { color: 'text-red-400'     },
  refunded:   { color: 'text-purple-400'  },
  in_process: { color: 'text-amber-300'   },
};

// Según docs MP Point: solo 'created' puede cancelarse vía API.
// 'at_terminal' requiere cancelación desde el lector físico.
// Según docs MP Point: solo 'created' puede cancelarse vía API.
// 'at_terminal' solo se puede cancelar desde el lector físico.
const CANCELLABLE = new Set(['created']);

function orderMeta(s)   { return { label: s || '—', ...( ORDER_STATUS[s]  || { icon: AlertCircle, color: 'text-slate-400', bg: 'bg-slate-400/10', border: 'border-slate-400/30', pulse: false }) }; }
function paymentMeta(s) { return { label: s || '—', ...(PAYMENT_STATUS[s] || { color: 'text-slate-400' }) }; }

function StatusBadge({ status, size = 'sm' }) {
  const { label, icon: Icon, color, bg, border, pulse } = orderMeta(status);
  const cls = size === 'lg'
    ? 'px-3 py-1.5 text-sm gap-1.5 rounded-xl'
    : 'px-2 py-0.5 text-xs gap-1 rounded-full';
  return (
    <span className={`inline-flex items-center border font-medium ${cls} ${color} ${bg} ${border}`}>
      <Icon size={size === 'lg' ? 14 : 11} className={pulse ? 'status-pulse' : ''} />
      {label}
    </span>
  );
}

/* ── Payment method icons ─────────────────────────────────── */

const METHOD_META = {
  credit_card: { label: 'Tarjeta crédito', icon: CreditCard, color: 'text-violet-400', bg: 'bg-violet-400/10' },
  debit_card:  { label: 'Tarjeta débito',  icon: CreditCard, color: 'text-blue-400',   bg: 'bg-blue-400/10'   },
  qr:          { label: 'QR',              icon: QrCode,     color: 'text-emerald-400',bg: 'bg-emerald-400/10'},
  cash:        { label: 'Efectivo',        icon: Banknote,   color: 'text-amber-400',  bg: 'bg-amber-400/10'  },
  efectivo:    { label: 'Efectivo',        icon: Banknote,   color: 'text-amber-400',  bg: 'bg-amber-400/10'  },
};

function methodMeta(m) {
  return METHOD_META[m] || { label: m || '—', icon: CreditCard, color: 'text-slate-400', bg: 'bg-slate-400/10' };
}

/* ── Helpers ──────────────────────────────────────────────── */

function formatAmount(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(v);
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDateShort(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

/* ── Copy-to-clipboard button ─────────────────────────────── */

function CopyButton({ text, className = '' }) {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };
  return (
    <button
      onClick={handle}
      title={copied ? '¡Copiado!' : 'Copiar'}
      className={`inline-flex items-center justify-center w-6 h-6 rounded-md transition-all
        hover:bg-accent/10 ${copied ? 'text-emerald-400' : ''} ${className}`}
      style={!copied ? { color: 'var(--text-muted)' } : {}}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

/* ── JSON Viewer ──────────────────────────────────────────── */

function syntaxHighlight(json) {
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      if (/^"/.test(match))     return /:$/.test(match) ? `<span class="json-key">${match}</span>` : `<span class="json-str">${match}</span>`;
      if (/true|false/.test(match)) return `<span class="json-bool">${match}</span>`;
      if (/null/.test(match))       return `<span class="json-null">${match}</span>`;
      return `<span class="json-num">${match}</span>`;
    }
  );
}

function JsonBlock({ data, label, icon: Icon }) {
  const [open, setOpen] = useState(false);
  if (!data) return null;

  const parsed = (() => {
    try { return JSON.stringify(typeof data === 'string' ? JSON.parse(data) : data, null, 2); }
    catch { return String(data); }
  })();

  const keyCount = (() => {
    try { const v = typeof data === 'string' ? JSON.parse(data) : data; return Object.keys(v).length; }
    catch { return 0; }
  })();

  return (
    <div className="rounded-xl overflow-hidden border border-border">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-bg-elevated hover:bg-accent/5 transition-colors"
        style={{ color: 'var(--text-secondary)' }}
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          {Icon && <Icon size={14} />}
          {label}
          <span className="text-xs font-normal opacity-60">({keyCount} campos)</span>
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <pre
          className="text-xs font-mono leading-relaxed p-4 overflow-auto max-h-80"
          style={{ background: 'var(--bg)', color: 'var(--text-secondary)' }}
          dangerouslySetInnerHTML={{ __html: syntaxHighlight(parsed) }}
        />
      )}
    </div>
  );
}

/* ── Cancel Confirmation Modal ────────────────────────────── */

function CancelModal({ order, onClose, onSuccess }) {
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canCancel    = CANCELLABLE.has(order.status);
  const atTerminal   = order.status === 'at_terminal';

  const handleConfirm = async () => {
    setError(null);
    setLoading(true);
    try {
      await apiCancelOrder(order.order_id, password);
      onSuccess();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cancelar la orden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <Ban size={20} className="text-red-400" />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Cancelar orden</h3>
              <p className="text-xs font-mono mt-0.5" style={{ color: 'var(--text-muted)' }}>
                #{order.order_id}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-bg-elevated" style={{ color: 'var(--text-muted)' }}>
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {atTerminal ? (
            <div className="rounded-xl p-4 bg-amber-400/10 border border-amber-400/20 text-sm text-amber-300 space-y-1">
              <p className="font-medium">La orden ya está en el lector físico.</p>
              <p>Para cancelarla, presioná el botón de cancelación directamente en la terminal. La cancelación vía API solo está disponible mientras la orden esté en estado <code className="font-mono text-xs bg-amber-400/10 px-1 rounded">created</code>.</p>
            </div>
          ) : !canCancel ? (
            <div className="rounded-xl p-4 bg-amber-400/10 border border-amber-400/20 text-sm text-amber-300">
              La orden está en estado <strong>"{order.status}"</strong> y no puede cancelarse vía API.
              Solo se pueden cancelar órdenes en estado <code className="font-mono text-xs bg-amber-400/10 px-1 rounded">created</code>.
            </div>
          ) : (
            <>
              <div className="rounded-xl p-4 bg-red-400/10 border border-red-400/20 text-sm text-red-300">
                Esta acción es <strong>irreversible</strong>. La orden se cancelará en MercadoPago y en el sistema local.
              </div>

              <div>
                <label className="label">Ingresá tu contraseña para confirmar</label>
                <div className="relative">
                  <input
                    type={showPwd ? 'text' : 'password'}
                    className="input-base pr-10"
                    placeholder="Contraseña actual"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && password && handleConfirm()}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-400 flex items-center gap-1.5">
                  <AlertCircle size={14} /> {error}
                </p>
              )}
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm transition-colors border border-border hover:border-border-strong"
              style={{ color: 'var(--text-secondary)' }}
            >
              {canCancel ? 'No cancelar' : 'Entendido'}
            </button>
            {canCancel && (
              <button
                onClick={handleConfirm}
                disabled={!password || loading}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                {loading ? 'Cancelando…' : 'Sí, cancelar orden'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Refund Modal ─────────────────────────────────────────── */

function RefundModal({ orderId, maxAmount, onClose, onSuccess }) {
  const [amount,   setAmount]   = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const parsedAmount  = amount.trim() !== '' ? parseFloat(amount.replace(',', '.')) : null;
  const isPartial     = parsedAmount !== null && parsedAmount < maxAmount;
  const amountInvalid = parsedAmount !== null && (parsedAmount <= 0 || parsedAmount > maxAmount);

  const handleConfirm = async () => {
    setError(null);
    setLoading(true);
    try {
      await apiRefundOrder(orderId, password, parsedAmount);
      onSuccess();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al realizar la devolución');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
           style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
              <RotateCcw size={20} className="text-violet-400" />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Devolver pago</h3>
              <p className="text-xs font-mono mt-0.5" style={{ color: 'var(--text-muted)' }}>#{orderId}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-bg-elevated" style={{ color: 'var(--text-muted)' }}>
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {/* Tipo de devolución */}
          <div className="rounded-xl p-4 bg-violet-400/10 border border-violet-400/20 text-sm text-violet-300">
            Monto original: <strong>{formatAmount(maxAmount)}</strong>. Dejá el campo vacío para devolución total.
          </div>

          {/* Monto parcial */}
          <div>
            <label className="label">Monto a devolver (vacío = total)</label>
            <input
              className="input-base"
              placeholder={`Ej: ${formatAmount(maxAmount)}`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {amountInvalid && (
              <p className="text-xs text-red-400 mt-1">
                Debe ser mayor a 0 y menor o igual a {formatAmount(maxAmount)}
              </p>
            )}
            {isPartial && !amountInvalid && (
              <p className="text-xs text-violet-400 mt-1">
                Devolución parcial: {formatAmount(parsedAmount)}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="label">Contraseña para confirmar</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                className="input-base pr-10"
                placeholder="Contraseña actual"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && password && !amountInvalid && handleConfirm()}
                autoFocus
              />
              <button type="button" onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: 'var(--text-muted)' }}>
                {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-400 flex items-center gap-1.5">
              <AlertCircle size={14} /> {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose}
                    className="px-4 py-2 rounded-lg text-sm border border-border hover:border-border-strong transition-colors"
                    style={{ color: 'var(--text-secondary)' }}>
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={!password || loading || amountInvalid}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-violet-500 hover:bg-violet-600 text-white
                         transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? 'Procesando…' : isPartial ? `Devolver ${formatAmount(parsedAmount)}` : 'Devolver monto total'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── MP Live Panel ────────────────────────────────────────── */

function MpOrderContent({ data }) {
  const payment = data?.transactions?.payments?.[0];
  const { color: payColor } = paymentMeta(payment?.status);

  return (
    <div className="space-y-3">
      {/* Status header */}
      <div className="rounded-xl p-4 bg-bg-elevated space-y-3">
        <div>
          <p className="text-xs font-mono mb-1" style={{ color: 'var(--text-muted)' }}>status</p>
          <StatusBadge status={data.status} size="lg" />
        </div>
        {data.status_detail && (
          <div>
            <p className="text-xs font-mono mb-1" style={{ color: 'var(--text-muted)' }}>status_detail</p>
            <span className="text-sm font-mono font-medium" style={{ color: 'var(--text-secondary)' }}>
              {data.status_detail}
            </span>
          </div>
        )}
        {data.total_amount != null && (
          <div>
            <p className="text-xs font-mono mb-1" style={{ color: 'var(--text-muted)' }}>total_amount</p>
            <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {formatAmount(data.total_amount)}
            </p>
          </div>
        )}
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-2">
        <InfoField icon={Calendar} label="date_created"      value={formatDate(data.date_created || data.created_date)} />
        <InfoField icon={Clock}    label="date_last_updated" value={formatDate(data.date_last_updated || data.last_updated_date)} />
      </div>

      {/* transactions.payments[0] */}
      {payment && (
        <div className="rounded-xl overflow-hidden border border-border">
          <div className="px-4 py-2 bg-bg-elevated border-b border-border">
            <p className="text-xs font-mono font-semibold" style={{ color: 'var(--text-muted)' }}>
              transactions.payments[0]
            </p>
          </div>
          <div className="divide-y divide-border">
            {payment.amount != null && (
              <StatusRow label="amount" value={
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {formatAmount(payment.amount)}
                </span>
              } />
            )}
            <StatusRow label="status" value={
              <span className={`text-sm font-medium font-mono ${payColor}`}>{payment.status || '—'}</span>
            } />
            {payment.status_detail && (
              <StatusRow label="status_detail" value={
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-bg-elevated"
                      style={{ color: 'var(--text-secondary)' }}>
                  {payment.status_detail}
                </span>
              } />
            )}
            {payment.payment_type_id && (
              <StatusRow label="payment_type_id" value={
                <span className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                  {payment.payment_type_id}
                </span>
              } />
            )}
            {payment.payment_method_id && (
              <StatusRow label="payment_method_id" value={
                <span className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                  {payment.payment_method_id}
                </span>
              } />
            )}
            {payment.installments != null && (
              <StatusRow label="installments" value={
                <span className="text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                  {payment.installments}
                </span>
              } />
            )}
          </div>
        </div>
      )}

      {/* Full JSON */}
      <JsonBlock data={data} label="Respuesta completa" icon={Receipt} />
    </div>
  );
}

function MpStatusPanel({ orderId, onClose, onCancelled }) {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [showRefund, setShowRefund] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getOrderMpStatus(orderId);
      setData(d);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error al consultar MP');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCancelled = useCallback(() => {
    setData((d) => d ? { ...d, status: 'canceled', status_detail: 'canceled_by_admin' } : d);
    setShowCancel(false);
    onCancelled?.();
  }, [onCancelled]);

  // Positioned immediately to the left of the DB panel (which is 540px from right edge)
  return (
    <div
      className="slide-over fixed top-0 h-full z-50 flex flex-col"
      style={{
        right:  'min(540px, 96vw)',
        width:  'min(460px, calc(100vw - min(540px, 96vw) - 8px))',
        background:  'var(--bg-surface)',
        borderLeft:  '1px solid var(--border)',
        borderRight: '1px solid var(--border)',
      }}
    >
      {/* Header */}
      <div
        className="shrink-0 px-5 pt-4 pb-3 border-b border-border"
        style={{ background: 'linear-gradient(135deg, rgb(0 166 80 / 0.08) 0%, transparent 60%)' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Estado en MercadoPago
            </span>
            <span className="text-xs px-1.5 py-0.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-400 font-medium">
              Live
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={fetchData}
              title="Actualizar"
              disabled={loading}
              className="p-1.5 rounded-lg hover:bg-bg-elevated transition-colors disabled:opacity-40"
              style={{ color: 'var(--text-muted)' }}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-bg-elevated transition-colors"
                    style={{ color: 'var(--text-muted)' }}>
              <X size={16} />
            </button>
          </div>
        </div>
        <p className="text-xs font-mono mt-1.5" style={{ color: 'var(--text-muted)' }}>
          #{orderId}
        </p>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {loading ? (
          <div className="flex justify-center pt-16"><Spinner /></div>
        ) : error ? (
          <div className="rounded-xl p-4 bg-red-400/10 border border-red-400/20 text-sm text-red-400">
            <AlertCircle size={14} className="inline mr-1.5" />{error}
          </div>
        ) : data ? (
          <MpOrderContent data={data} />
        ) : null}
      </div>

      {/* Footer — acciones según estado de MP */}
      {data && (
        (() => {
          const payment     = data?.transactions?.payments?.[0];
          const canCancel   = CANCELLABLE.has(data.status);
          const atTerminal  = data.status === 'at_terminal';
          const canRefund   = payment?.status === 'approved';
          if (!canCancel && !atTerminal && !canRefund) return null;
          return (
            <div className="shrink-0 px-5 py-4 border-t border-border space-y-2">
              {/* Cancelar */}
              {canCancel && (
                <button onClick={() => setShowCancel(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                             bg-red-500/10 border border-red-500/20 text-red-400
                             hover:bg-red-500/20 transition-colors text-sm font-medium">
                  <Ban size={15} /> Cancelar esta orden
                </button>
              )}
              {/* at_terminal info */}
              {atTerminal && !canCancel && (
                <div className="rounded-xl px-4 py-3 bg-amber-400/10 border border-amber-400/20 text-xs text-amber-300 flex items-start gap-2">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <span>La orden está en la terminal. Para cancelarla, presioná el botón en el lector físico.</span>
                </div>
              )}
              {/* Devolver */}
              {canRefund && (
                <button onClick={() => setShowRefund(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                             bg-violet-500/10 border border-violet-500/20 text-violet-400
                             hover:bg-violet-500/20 transition-colors text-sm font-medium">
                  <RotateCcw size={15} /> Devolver pago
                </button>
              )}
            </div>
          );
        })()
      )}

      {/* Cancel modal */}
      {showCancel && (
        <CancelModal
          order={{ order_id: orderId, status: data?.status }}
          onClose={() => setShowCancel(false)}
          onSuccess={handleCancelled}
        />
      )}

      {/* Refund modal */}
      {showRefund && (
        <RefundModal
          orderId={orderId}
          maxAmount={parseFloat(data?.transactions?.payments?.[0]?.amount || 0)}
          onClose={() => setShowRefund(false)}
          onSuccess={() => {
            fetchData(); // refresca el panel con el nuevo estado de MP
            onCancelled?.(); // invalida el query de la lista
          }}
        />
      )}
    </div>
  );
}

/* ── Detail Slide-Over ────────────────────────────────────── */

function OrderDetail({ order: initialOrder, onClose, onCancelled }) {
  const [order, setOrder] = useState(initialOrder);
  const [showMpPanel, setShowMpPanel] = useState(false);

  const handleCancelled = useCallback(() => {
    setOrder((o) => ({ ...o, status: 'canceled', payment_status: 'canceled', payment_status_detail: 'canceled_by_admin' }));
    onCancelled?.();
  }, [onCancelled]);

  const { label: pmLabel, icon: PmIcon, color: pmColor, bg: pmBg } = methodMeta(order.payment_method);
  const { label: payLabel, color: payColor } = paymentMeta(order.payment_status);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />

      {/* Panel */}
      <div
        className="slide-over fixed right-0 top-0 h-full z-50 flex flex-col"
        style={{ width: 'min(540px, 96vw)', background: 'var(--bg-surface)', borderLeft: '1px solid var(--border)' }}
      >
        {/* Header gradient */}
        <div
          className="shrink-0 relative px-6 pt-5 pb-4"
          style={{ background: `linear-gradient(135deg, rgb(var(--accent-rgb) / 0.12) 0%, transparent 55%)` }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-bg-elevated transition-colors"
            style={{ color: 'var(--text-muted)' }}
          >
            <X size={18} />
          </button>

          {/* Amount + status */}
          <div className="flex items-start gap-4 pr-8">
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>MONTO</p>
              <p className="text-3xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
                {formatAmount(order.amount)}
              </p>
            </div>
            <div className="ml-auto">
              <StatusBadge status={order.status} size="lg" />
            </div>
          </div>

          {/* Order ID with copy */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 font-mono text-xs px-2 py-1 rounded-lg bg-bg-elevated"
                 style={{ color: 'var(--text-muted)' }}>
              <Hash size={10} />
              {order.order_id}
              <CopyButton text={order.order_id} />
            </div>
            {order.external_reference && (
              <div className="text-xs px-2 py-1 rounded-lg bg-bg-elevated flex items-center gap-1"
                   style={{ color: 'var(--text-secondary)' }}>
                <FileText size={10} />
                {order.external_reference}
                <CopyButton text={order.external_reference} />
              </div>
            )}
          </div>
        </div>

        {/* Body scroll */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* Payment method — prominent card */}
          <div className="rounded-xl p-4 bg-bg-elevated flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${pmBg}`}>
              <PmIcon size={22} className={pmColor} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs mb-0.5" style={{ color: 'var(--text-muted)' }}>Medio de pago</p>
              <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{pmLabel}</p>
            </div>
          </div>

          {/* Estados de pago */}
          <div className="rounded-xl overflow-hidden border border-border">
            <div className="px-4 py-2 bg-bg-elevated border-b border-border flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Estado de pago</p>
            </div>
            <div className="divide-y divide-border">
              <StatusRow
                label="payment_status"
                value={
                  <span className={`text-sm font-medium font-mono ${payColor}`}>{order.payment_status || '—'}</span>
                }
              />
              <StatusRow
                label="payment_status_detail"
                value={
                  <span className="text-xs font-mono px-2 py-0.5 rounded bg-bg-elevated"
                        style={{ color: 'var(--text-secondary)' }}>
                    {order.payment_status_detail || '—'}
                  </span>
                }
              />
            </div>
          </div>

          {/* Key data grid */}
          <div className="grid grid-cols-2 gap-2">
            <InfoField icon={Store}    label="Sucursal"  value={order.store_name || order.store_external_id || '—'} />
            <InfoField icon={MapPin}   label="POS"       value={order.pos_number || order.pos_name || '—'} />
            <InfoField icon={Terminal} label="Terminal"  value={order.mp_terminal_id || '—'} mono />
            {order.description && (
              <InfoField icon={FileText} label="Descripción" value={order.description} className="col-span-2" />
            )}
            <InfoField icon={Calendar} label="Creada"    value={formatDate(order.created_date)} />
            <InfoField icon={Clock}    label="Actualizada" value={formatDate(order.updated_date)} />
          </div>

          {/* JSON sections */}
          <div className="space-y-2">
            <JsonBlock data={order.mp_request}  label="Solicitud enviada a MP"   icon={Send}    />
            <JsonBlock data={order.mp_response} label="Respuesta de MP"          icon={Receipt} />
          </div>
        </div>

        {/* Footer — solo botón de consulta MP */}
        <div className="shrink-0 px-6 py-4 border-t border-border">
          <button
            onClick={() => setShowMpPanel((v) => !v)}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                       border transition-colors text-sm font-medium
                       ${showMpPanel
                         ? 'bg-accent/10 border-accent/30 text-accent hover:bg-accent/15'
                         : 'bg-bg-elevated border-border hover:border-border-strong'}`}
            style={!showMpPanel ? { color: 'var(--text-secondary)' } : {}}
          >
            <Wifi size={15} />
            {showMpPanel ? 'Ocultar datos de MP' : 'Consultar estado en MP'}
          </button>
        </div>
      </div>

      {/* MP live panel — aparece a la izquierda del panel de detalle */}
      {showMpPanel && (
        <MpStatusPanel
          orderId={order.order_id}
          onClose={() => setShowMpPanel(false)}
          onCancelled={handleCancelled}
        />
      )}

    </>
  );
}

function StatusRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{label}</span>
      {value}
    </div>
  );
}

function InfoField({ icon: Icon, label, value, mono, className = '' }) {
  return (
    <div className={`rounded-xl p-3 bg-bg-elevated ${className}`}>
      <div className="flex items-center gap-1.5 mb-1" style={{ color: 'var(--text-muted)' }}>
        <Icon size={11} />
        <span className="text-xs">{label}</span>
      </div>
      <p
        className={`text-sm font-medium truncate ${mono ? 'font-mono text-xs' : ''}`}
        style={{ color: 'var(--text-primary)' }}
      >
        {value}
      </p>
    </div>
  );
}

/* ── Filters ──────────────────────────────────────────────── */

const ORDER_STATUS_OPTS   = ['created', 'processed', 'canceled', 'expired', 'failed', 'refunded'];
const PAYMENT_STATUS_OPTS = ['created', 'processed', 'canceled', 'cancelled', 'expired', 'failed', 'refunded'];
const PAYMENT_DETAIL_OPTS = [
  'accredited', 'cancel_by_terminal', 'canceled', 'canceled_by_api',
  'expired', 'failed', 'insufficient_amount', 'partially_refunded', 'refunded',
];

function FiltersBar({ stores, filters, onChange, onReset }) {
  const f = (key) => (e) => onChange({ ...filters, [key]: e.target.value, page: 1 });

  return (
    <div className="card space-y-3">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="min-w-[150px] flex-1">
          <label className="label">Sucursal</label>
          <select className="input-base" value={filters.storeId} onChange={f('storeId')}>
            <option value="">Todas</option>
            {stores?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div className="min-w-[130px] flex-1">
          <label className="label">Estado de orden</label>
          <select className="input-base" value={filters.status} onChange={f('status')}>
            <option value="">Todos</option>
            {ORDER_STATUS_OPTS.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div className="min-w-[130px] flex-1">
          <label className="label">Estado de pago</label>
          <select className="input-base" value={filters.paymentStatus} onChange={f('paymentStatus')}>
            <option value="">Todos</option>
            {PAYMENT_STATUS_OPTS.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div className="min-w-[160px] flex-1">
          <label className="label">Detalle de pago</label>
          <select className="input-base" value={filters.paymentStatusDetail} onChange={f('paymentStatusDetail')}>
            <option value="">Todos</option>
            {PAYMENT_DETAIL_OPTS.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div className="min-w-[130px] flex-1">
          <label className="label">POS</label>
          <input
            className="input-base"
            placeholder="Ej: SUC001POS001"
            value={filters.posNumber}
            onChange={f('posNumber')}
          />
        </div>

        <div className="min-w-[120px] flex-1">
          <label className="label">Medio de pago</label>
          <select className="input-base" value={filters.paymentMethod} onChange={f('paymentMethod')}>
            <option value="">Todos</option>
            {[['credit_card','Tarjeta crédito'],['debit_card','Tarjeta débito'],['qr','QR']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        <div className="min-w-[120px] flex-1">
          <label className="label">Desde</label>
          <input type="date" className="input-base" value={filters.dateFrom} onChange={f('dateFrom')} />
        </div>

        <div className="min-w-[120px] flex-1">
          <label className="label">Hasta</label>
          <input type="date" className="input-base" value={filters.dateTo} onChange={f('dateTo')} />
        </div>

        <button
          onClick={onReset}
          className="px-3 py-2 rounded-lg text-sm border border-border hover:border-border-strong transition-colors"
          style={{ color: 'var(--text-muted)' }}
        >
          Limpiar
        </button>
      </div>
    </div>
  );
}

/* ── Orders Table ─────────────────────────────────────────── */

const TABLE_HEADERS = ['ID', 'Referencia', 'Sucursal / POS', 'Medio de pago', 'Monto', 'status', 'payment_status', 'payment_detail', 'Fecha'];

function OrdersTable({ orders, loading, onSelect }) {
  return (
    <div className="card !p-0 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
              {TABLE_HEADERS.map((h) => {
                const isDbField = ['status','payment_status','payment_detail'].includes(h);
                return (
                  <th
                    key={h}
                    className={`px-4 py-3 text-left text-xs whitespace-nowrap
                      ${isDbField ? 'font-mono font-medium' : 'font-semibold uppercase tracking-wider'}`}
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {h}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_HEADERS.length} className="py-16 text-center">
                  <Spinner />
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={TABLE_HEADERS.length}>
                  <div className="flex flex-col items-center justify-center py-14 gap-2"
                       style={{ color: 'var(--text-muted)' }}>
                    <ShoppingBag size={36} strokeWidth={1.2} />
                    <p className="text-sm">No hay órdenes con los filtros actuales</p>
                  </div>
                </td>
              </tr>
            ) : orders.map((o) => (
              <OrderRow key={o.id} order={o} onClick={onSelect} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrderRow({ order, onClick }) {
  const { icon: PmIcon, color: pmColor } = methodMeta(order.payment_method);
  const { label: payLabel, color: payColor } = paymentMeta(order.payment_status);

  return (
    <tr
      onClick={() => onClick(order)}
      className="cursor-pointer transition-colors hover:bg-bg-elevated"
      style={{ borderBottom: '1px solid var(--border)' }}
    >
      <td className="px-4 py-3">
        <span className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>
          {order.order_id ? `…${String(order.order_id).slice(-8)}` : '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm font-medium truncate max-w-[140px]" style={{ color: 'var(--text-primary)' }}>
          {order.external_reference || '—'}
        </p>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
          <Store size={12} className="shrink-0" />
          <span className="text-sm truncate max-w-[110px]">{order.store_name || order.store_external_id || '—'}</span>
        </div>
        {order.pos_number && (
          <div className="text-xs font-mono mt-0.5" style={{ color: 'var(--text-muted)' }}>{order.pos_number}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <PmIcon size={13} className={`shrink-0 ${pmColor}`} />
          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {methodMeta(order.payment_method).label}
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {formatAmount(order.amount)}
        </span>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={order.status} />
      </td>
      <td className="px-4 py-3">
        <span className={`text-xs font-medium font-mono ${payColor}`}>{order.payment_status || '—'}</span>
      </td>
      <td className="px-4 py-3">
        {order.payment_status_detail
          ? <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-bg-elevated"
                  style={{ color: 'var(--text-secondary)' }}>{order.payment_status_detail}</span>
          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-muted)' }}>
        {formatDateShort(order.created_date)}
      </td>
    </tr>
  );
}

/* ── Pagination ───────────────────────────────────────────── */

function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  const range = [];
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  for (let p = start; p <= end; p++) range.push(p);

  return (
    <div className="flex items-center justify-center gap-1 pt-2">
      <PBtn label="‹" disabled={page <= 1}          onClick={() => onPage(page - 1)} />
      {start > 1 && <><PBtn label="1" onClick={() => onPage(1)} />{start > 2 && <span className="px-1 text-xs" style={{ color: 'var(--text-muted)' }}>…</span>}</>}
      {range.map((p) => <PBtn key={p} label={p} active={p === page} onClick={() => onPage(p)} />)}
      {end < totalPages && <><span className="px-1 text-xs" style={{ color: 'var(--text-muted)' }}>…</span><PBtn label={totalPages} onClick={() => onPage(totalPages)} /></>}
      <PBtn label="›" disabled={page >= totalPages} onClick={() => onPage(page + 1)} />
    </div>
  );
}

function PBtn({ label, active, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`min-w-[32px] h-8 px-2 rounded-lg text-sm transition-colors
        ${active   ? 'bg-accent text-white font-semibold' : ''}
        ${disabled ? 'opacity-30 cursor-default' : 'hover:bg-bg-elevated cursor-pointer'}`}
      style={!active ? { color: 'var(--text-secondary)' } : {}}
    >
      {label}
    </button>
  );
}

/* ── Archive Batch Modal ──────────────────────────────────── */

function ArchiveBatchModal({ onClose, onSuccess }) {
  const { selectedClientId } = useClientStore();
  const user = useAuthStore((s) => s.user);
  const [olderThanDays, setOlderThanDays] = useState('30');
  const [statuses, setStatuses] = useState(['processed', 'canceled']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const toggleStatus = (s) =>
    setStatuses((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);

  const handleConfirm = async () => {
    setError(null);
    setLoading(true);
    try {
      const clientId = user?.role === 'superadmin' ? selectedClientId : undefined;
      const data = await apiArchiveBatch({
        ...(clientId ? { clientId } : {}),
        olderThanDays: parseInt(olderThanDays, 10),
        statuses,
      });
      setResult(data);
      onSuccess?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al archivar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
           style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
        <div className="px-6 pt-6 pb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <Archive size={20} className="text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Pasar a histórico</h3>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                Mueve órdenes finalizadas de la tabla activa al histórico
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-bg-elevated" style={{ color: 'var(--text-muted)' }}>
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {result ? (
            <div className="rounded-xl p-4 bg-emerald-400/10 border border-emerald-400/20 text-sm text-emerald-300 text-center">
              <CheckCircle2 size={24} className="mx-auto mb-2" />
              <p className="font-semibold">{result.message}</p>
            </div>
          ) : (
            <>
              <div>
                <label className="label">Estados a archivar</label>
                <div className="flex gap-2 flex-wrap">
                  {['processed', 'canceled'].map((s) => (
                    <button
                      key={s}
                      onClick={() => toggleStatus(s)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-mono font-medium border transition-colors
                        ${statuses.includes(s)
                          ? 'bg-amber-400/15 border-amber-400/40 text-amber-300'
                          : 'border-border text-slate-400 hover:border-slate-500'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">Antigüedad mínima (días)</label>
                <input
                  type="number"
                  className="input-base"
                  value={olderThanDays}
                  min="1"
                  max="3650"
                  onChange={(e) => setOlderThanDays(e.target.value)}
                />
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  Se archivarán órdenes creadas hace más de {olderThanDays} días
                </p>
              </div>
              {error && (
                <p className="text-sm text-red-400 flex items-center gap-1.5">
                  <AlertCircle size={14} /> {error}
                </p>
              )}
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose}
                    className="px-4 py-2 rounded-lg text-sm border border-border hover:border-border-strong transition-colors"
                    style={{ color: 'var(--text-secondary)' }}>
              {result ? 'Cerrar' : 'Cancelar'}
            </button>
            {!result && (
              <button
                onClick={handleConfirm}
                disabled={loading || statuses.length === 0 || !olderThanDays}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white
                           transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                {loading ? 'Archivando…' : 'Confirmar archivado'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Archive reason badge ─────────────────────────────────── */

const REASON_LABELS = {
  manual_archive:    { label: 'Manual',            color: 'text-slate-400' },
  pos_deleted:       { label: 'POS eliminado',     color: 'text-amber-400' },
  store_deleted:     { label: 'Suc. eliminada',    color: 'text-amber-400' },
  client_offboarded: { label: 'Offboarding',       color: 'text-red-400'   },
};

function ReasonBadge({ reason }) {
  const { label, color } = REASON_LABELS[reason] || { label: reason || '—', color: 'text-slate-400' };
  return <span className={`text-xs font-medium ${color}`}>{label}</span>;
}

/* ── Main Page ────────────────────────────────────────────── */

const EMPTY_FILTERS = {
  storeId: '', status: '', paymentStatus: '', paymentStatusDetail: '',
  paymentMethod: '', posNumber: '', dateFrom: '', dateTo: '', page: 1,
};

const ARCHIVE_HEADERS = ['ID', 'Referencia', 'Sucursal / POS', 'Monto', 'status', 'payment_status', 'Fecha orden', 'Archivado el', 'Motivo'];

function ArchiveOrdersTable({ orders, loading }) {
  return (
    <div className="card !p-0 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
              {ARCHIVE_HEADERS.map((h) => (
                <th key={h}
                    className="px-4 py-3 text-left text-xs whitespace-nowrap font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--text-muted)' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={ARCHIVE_HEADERS.length} className="py-16 text-center"><Spinner /></td></tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={ARCHIVE_HEADERS.length}>
                  <div className="flex flex-col items-center justify-center py-14 gap-2" style={{ color: 'var(--text-muted)' }}>
                    <ArchiveRestore size={36} strokeWidth={1.2} />
                    <p className="text-sm">No hay órdenes en el histórico con los filtros actuales</p>
                  </div>
                </td>
              </tr>
            ) : orders.map((o) => {
              const { label: payLabel, color: payColor } = paymentMeta(o.payment_status);
              return (
                <tr key={o.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>
                      {o.order_id ? `…${String(o.order_id).slice(-8)}` : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium truncate max-w-[140px]" style={{ color: 'var(--text-primary)' }}>
                      {o.external_reference || '—'}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
                      <Store size={12} className="shrink-0" />
                      <span className="text-sm truncate max-w-[110px]">{o.store_name || o.store_external_id || '—'}</span>
                    </div>
                    {o.pos_number && <div className="text-xs font-mono mt-0.5" style={{ color: 'var(--text-muted)' }}>{o.pos_number}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{formatAmount(o.amount)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium font-mono ${payColor}`}>{o.payment_status || '—'}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-muted)' }}>
                    {formatDateShort(o.created_date)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-muted)' }}>
                    {formatDateShort(o.archived_at)}
                  </td>
                  <td className="px-4 py-3">
                    <ReasonBadge reason={o.archived_reason} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Orders() {
  const user                 = useAuthStore((s) => s.user);
  const { selectedClientId } = useClientStore();
  const queryClient          = useQueryClient();
  const location             = useLocation();
  const [source, setSource]          = useState('active');
  const [filters, setFilters]        = useState(() => ({ ...EMPTY_FILTERS, ...(location.state?.initFilters || {}) }));
  const [selected, setSelected]      = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [exporting, setExporting]    = useState(false);

  const { data: storesData }                          = useStores();
  const { data: activeData,  isLoading: activeLoading,  isFetching: activeFetching,  error: activeError  } = useOrders(filters);
  const { data: archiveData, isLoading: archiveLoading, isFetching: archiveFetching, error: archiveError } = useOrdersArchive(filters);

  const data      = source === 'active' ? activeData      : archiveData;
  const isLoading = source === 'active' ? activeLoading   : archiveLoading;
  const isFetching= source === 'active' ? activeFetching  : archiveFetching;
  const error     = source === 'active' ? activeError     : archiveError;

  const orders     = data?.orders     || [];
  const total      = data?.total      || 0;
  const totalPages = data?.totalPages || 1;

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    queryClient.invalidateQueries({ queryKey: ['orders-archive'] });
  }, [queryClient]);

  const handlePage      = useCallback((p) => setFilters((f) => ({ ...f, page: p })), []);
  const handleCancelled = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['orders'] });
  }, [queryClient]);

  const switchSource = (s) => { setSource(s); setFilters((f) => ({ ...f, page: 1 })); setSelected(null); };

  const handleExportArchive = useCallback(async () => {
    setExporting(true);
    try {
      const clientId = user?.role === 'superadmin' ? selectedClientId : undefined;
      const { storeId, posNumber, status, paymentStatus, paymentStatusDetail, paymentMethod, dateFrom, dateTo } = filters;
      const data = await apiExportArchive({
        ...(clientId ? { clientId } : {}),
        storeId, posNumber, status, paymentStatus, paymentStatusDetail, paymentMethod, dateFrom, dateTo,
      });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders_archive_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al exportar');
    } finally {
      setExporting(false);
    }
  }, [filters, user, selectedClientId]);

  if (user?.role === 'superadmin' && !selectedClientId) {
    return <NoClientSelected entity="órdenes" />;
  }

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
            <ShoppingBag size={18} className="text-accent" />
          </div>
          <div>
            <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Órdenes</h2>
            {!isLoading && (
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {total.toLocaleString('es-AR')} {total === 1 ? 'orden' : 'órdenes'}
                {source === 'archive' && ' en histórico'}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Source tabs */}
          <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={() => switchSource('active')}
              className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors
                ${source === 'active' ? 'bg-accent/15 text-accent' : 'hover:bg-bg-elevated'}`}
              style={source !== 'active' ? { color: 'var(--text-secondary)' } : {}}
            >
              <ShoppingBag size={13} /> Activas
            </button>
            <button
              onClick={() => switchSource('archive')}
              className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors border-l
                ${source === 'archive' ? 'bg-amber-400/10 text-amber-400 border-l-border' : 'hover:bg-bg-elevated'}`}
              style={{ borderLeftColor: 'var(--border)', ...(source !== 'archive' ? { color: 'var(--text-secondary)' } : {}) }}
            >
              <Archive size={13} /> Históricas
            </button>
          </div>

          {/* Archive button — only on active tab */}
          {source === 'active' && (
            <button
              onClick={() => setShowArchiveModal(true)}
              title="Pasar órdenes finalizadas al histórico"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-all
                         hover:border-amber-400/40 hover:text-amber-400"
              style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
            >
              <ArchiveRestore size={14} />
              <span className="hidden sm:inline">Archivar</span>
            </button>
          )}

          {/* Export button — only on archive tab */}
          {source === 'archive' && (
            <button
              onClick={handleExportArchive}
              disabled={exporting || isLoading}
              title="Descargar histórico como JSON"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-all
                         hover:border-emerald-400/40 hover:text-emerald-400
                         disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              <span className="hidden sm:inline">{exporting ? 'Exportando…' : 'Descargar'}</span>
            </button>
          )}

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            title="Refrescar lista"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm
                       transition-all hover:border-accent/40 hover:text-accent
                       disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <FiltersBar
        stores={storesData}
        filters={filters}
        onChange={setFilters}
        onReset={() => setFilters(EMPTY_FILTERS)}
      />

      {/* Error banner */}
      {error && (
        <div className="rounded-xl px-4 py-3 bg-red-400/10 border border-red-400/20 text-sm text-red-400">
          Error al cargar órdenes: {error.response?.data?.error || error.message}
        </div>
      )}

      {/* Tables */}
      {!error && source === 'active' && (
        <OrdersTable orders={orders} loading={isLoading} onSelect={setSelected} />
      )}
      {!error && source === 'archive' && (
        <ArchiveOrdersTable orders={orders} loading={isLoading} />
      )}

      {/* Pagination */}
      <Pagination page={filters.page} totalPages={totalPages} onPage={handlePage} />

      {/* Detail slide-over (solo activas) */}
      {selected && source === 'active' && (
        <OrderDetail
          order={selected}
          onClose={() => setSelected(null)}
          onCancelled={handleCancelled}
        />
      )}

      {/* Archive modal */}
      {showArchiveModal && (
        <ArchiveBatchModal
          onClose={() => setShowArchiveModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['orders-archive'] });
          }}
        />
      )}
    </div>
  );
}
